// src/contexts/SubscriptionContext.js
// Centralized subscription manager providing dual-source fetch (PaymentsServiceApi + mirror PaymentsApi),
// normalization, activation polling, and unified status for gating.
//
// Responsibilities:
// - Fetch primary subscription (Stripe-direct via PaymentsServiceApi)
// - Fetch mirror subscription (data service) when primary missing or on refresh
// - Normalize shape to { subscriptionId, status, planAlias, cancelAtPeriodEnd, currentPeriodEnd }
// - Provide derived display subscription (prefers mirror if primary missing)
// - Offer refresh & polling utilities
// - Expose initialized/loading flags so consumers (e.g., ProtectedRoute) wait before gating
//
// Edge cases handled:
// - Mirror updates first (webhook latency) => prefer mirror when primary lacks id
// - Polling stops on terminal statuses or when active/trialing reached
// - Feature flag disabled => initializes immediately with null subscription

import React, { createContext, useContext, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { PaymentsServiceApi } from '../services/PaymentsServiceApi';
import { PaymentsApi } from '../services/api/PaymentsApi';
import { isPaymentsEnabled } from '../config/featureFlags';
import { useAuth } from './AuthContext';
import { getTrialStatus } from '../hooks/useTrialAccess';
import { resolveTrialStart } from '../hooks/usePremiumAccess';

const SubscriptionContext = createContext(null);

// --- Price ID to Tier Mapping ---
// This maps Stripe price IDs to their corresponding tiers
// Update this mapping when adding new price tiers
const PRICE_ID_TO_TIER = {
  // Production price IDs
  'price_1SX1kHAkfpgL49nxoWHJks7C': 'basic', // Basic plan ($4.99/month) - PROD
  'price_1SX1ljAkfpgL49nxO7YHDx0k': 'pro',   // Pro plan ($19.99/month) - PROD
  // Development/Test price IDs
  'price_1SX7QxAkfpgL49nx9NoCQb2S': 'basic', // Basic plan - DEV
};

// --- Subscription Tier Utilities ---

/**
 * Determines if a user is on the free tier
 * Free tier users are those who:
 * - Have no subscription at all (null/undefined)
 * - Have no subscriptionId
 * - Have a plan alias indicating free access ('free', 'trial', 'beta')
 * - Have a zero-amount subscription
 * 
 * @param {Object|null} subscription - The subscription object from SubscriptionContext
 * @returns {boolean} True if user is on free tier
 */
export const isFreeTier = (subscription) => {
  // No subscription data at all = free tier
  if (!subscription) return true;
  
  // Check backend's tier assessment first (most authoritative)
  const backendTier = subscription.tierFromBackend || subscription.tier;
  if (backendTier === 'free' || backendTier === 'legacy-free') return true;
  if (backendTier === 'paid') return false;
  
  // No subscription ID = not subscribed to Stripe = free tier
  if (!subscription.subscriptionId) return true;
  
  // Check for explicit free plan aliases
  const freeAliases = ['free', 'trial', 'beta'];
  const planAlias = subscription.planAlias?.toLowerCase();
  if (planAlias && freeAliases.includes(planAlias)) return true;
  
  // Check if amount is 0 (free plan) - don't check for null as that's unknown
  if (subscription.amount === 0) return true;
  
  // If none of the above, user has a paid subscription
  return false;
};

/**
 * Check if a plan definition is a free plan
 * @param {Object} plan - Plan object with alias and amount
 * @returns {boolean} True if plan is free
 */
export const isPlanFree = (plan) => {
  if (!plan) return false;
  
  // Check for free alias
  const freeAliases = ['free', 'trial', 'beta'];
  const planAlias = plan.alias?.toLowerCase();
  if (planAlias && freeAliases.includes(planAlias)) return true;
  
  // Check if amount is 0
  if (plan.amount === 0) return true;
  
  return false;
};

/**
 * Determines if a user is on a paid tier
 * Paid tier users are those who:
 * - Have a valid subscription ID
 * - Have an active, trialing, or canceled (but still within period) status
 * - Are NOT on a free tier (checked via isFreeTier)
 * 
 * @param {Object|null} subscription - The subscription object from SubscriptionContext
 * @returns {boolean} True if user is on paid tier
 */
export const isPaidTier = (subscription) => {
  // No subscription = free tier
  if (!subscription || !subscription.subscriptionId) return false;
  
  // Normalize status
  const status = typeof subscription.status === 'string' 
    ? subscription.status.toLowerCase() 
    : subscription.status;
  
  // Incomplete subscriptions are NOT considered paid - payment not completed
  // This happens when user started upgrade but didn't complete Stripe checkout
  if (status === 'incomplete' || status === 'incomplete_expired') return false;
  
  // Admin-granted complimentary subscriptions are always considered paid (pro-tier)
  if (status === 'complimentary') {
    return true;
  }
  
  // Active or trialing subscriptions are paid
  const activeStatuses = ['active', 'trialing'];
  if (activeStatuses.includes(status)) {
    return !isFreeTier(subscription);
  }
  
  // Canceled subscriptions: check if still within the paid period
  if (status === 'canceled') {
    // If cancelAtPeriodEnd is true, OR if currentPeriodEnd is in the future, user may still have access
    const periodEnd = subscription.currentPeriodEnd;
    
    // cancelAtPeriodEnd=false means subscription was canceled immediately - user loses access now
    if (subscription.cancelAtPeriodEnd === false) {
      // Check if period already ended - if so, no access
      if (periodEnd) {
        const endTime = typeof periodEnd === 'number' ? periodEnd * 1000 : Date.parse(periodEnd);
        // Only grant access if period end is explicitly in the future
        if (!endTime || endTime <= Date.now()) {
          return false; // Period ended or invalid - no paid access
        }
      } else {
        return false; // No period end info and canceled immediately - no paid access
      }
    }
    
    // cancelAtPeriodEnd=true means user has access until period ends
    if (periodEnd) {
      const endTime = typeof periodEnd === 'number' ? periodEnd * 1000 : Date.parse(periodEnd);
      if (endTime && endTime > Date.now()) {
        return !isFreeTier(subscription);
      }
    }
    
    // No period end or period already ended - no paid access
    return false;
  }
  
  // All other statuses (past_due, unpaid, paused, etc.) are not paid
  return false;
};

/**
 * Gets the user's current tier as a string
 * 
 * @param {Object|null} subscription - The subscription object from SubscriptionContext
 * @returns {'free'|'basic'|'pro'|'custom'|'legacy-free'|'legacy-basic'|'legacy-pro'} The user's current tier
 */
export const getUserTier = (subscription) => {
  // Check if free first
  if (!isPaidTier(subscription)) return 'free';
  
  // Admin-granted complimentary access is always pro tier
  const status = typeof subscription?.status === 'string' ? subscription.status.toLowerCase() : '';
  if (status === 'complimentary') return 'pro';
  
  // Check backend's explicit tier field (most authoritative)
  const backendTier = subscription?.tierFromBackend || subscription?.tier;
  const tierLower = backendTier?.toLowerCase();
  
  // Accept legacy- prefix or standard tiers from backend
  if (tierLower && (tierLower.startsWith('legacy-') || ['free', 'basic', 'pro', 'custom'].includes(tierLower))) {
    return tierLower;
  }
  
  // Check priceId mapping (fallback when backend doesn't provide tier)
  const priceId = subscription?.priceId;
  if (priceId && PRICE_ID_TO_TIER[priceId]) {
    return PRICE_ID_TO_TIER[priceId];
  }
  
  // Infer tier from plan alias
  const planAlias = subscription?.planAlias?.toLowerCase();
  if (planAlias) {
    if (planAlias.includes('basic')) return 'basic';
    if (planAlias.includes('pro') || planAlias.includes('premium')) return 'pro';
  }
  
  // Infer from price amount (example thresholds - adjust as needed)
  const amount = subscription?.amount;
  if (typeof amount === 'number' && amount > 0) {
    // Basic tier: $0.01 - $14.99 (0-1499 cents)
    if (amount < 1500) return 'basic';
    // Pro tier: $15+ (1500+ cents)
    return 'pro';
  }
  
  // Default to custom for any paid tier we can't identify
  return 'custom';
};

/**
 * Check if user is on Basic tier specifically
 * @param {Object|null} subscription - The subscription object
 * @returns {boolean} True if user is on basic tier
 */
export const isBasicTier = (subscription) => {
  const tier = getUserTier(subscription);
  return tier === 'basic' || tier === 'legacy-basic';
};

/**
 * Check if user is on Pro tier specifically
 * @param {Object|null} subscription - The subscription object
 * @returns {boolean} True if user is on pro tier
 */
export const isProTier = (subscription) => {
  const tier = getUserTier(subscription);
  return tier === 'pro' || tier === 'legacy-pro';
};

/**
 * Check if a user can access a feature based on their tier
 * 
 * @param {Object|null} subscription - The subscription object from SubscriptionContext
 * @param {'free'|'paid'} requiredTier - The minimum tier required for the feature
 * @returns {boolean} True if user can access the feature
 */
export const canAccessFeature = (subscription, requiredTier) => {
  const userTier = getUserTier(subscription);
  
  // Normalize legacy tier prefix: legacy-free -> free, legacy-basic -> basic, etc.
  const normalizedTier = userTier.startsWith('legacy-') ? userTier.replace('legacy-', '') : userTier;
  
  // Map tier hierarchy: free < basic < pro
  const tierHierarchy = { free: 0, basic: 1, pro: 2 };
  
  // Handle legacy 'paid' requirement (basic or pro)
  if (requiredTier === 'paid') {
    return normalizedTier === 'basic' || normalizedTier === 'pro';
  }
  
  const userLevel = tierHierarchy[normalizedTier] ?? 0;
  const requiredLevel = tierHierarchy[requiredTier] ?? 0;
  
  return userLevel >= requiredLevel;
};

/**
 * Trial-aware version of isFreeTier.
 * Returns false (= has access) if user is on a paid plan OR inside the 7-day free trial.
 * Use this in UI gating instead of raw isFreeTier() to respect the trial window.
 *
 * @param {Object|null} subscription
 * @param {{ isInFreeTrial: boolean }} trialStatus - from useTrialAccess / getTrialStatus
 * @returns {boolean}
 */
export const isEffectivelyFree = (subscription, trialStatus) => {
  if (isPaidTier(subscription)) return false;
  if (trialStatus?.isInFreeTrial) return false;
  return true;
};

export function SubscriptionProvider({ children }) {
  const { user, userId } = useAuth();
  const paymentsEnabled = isPaymentsEnabled();

  // --- Free trial status (derived from user.firstLoginAt) ---
  const trialStatus = useMemo(() => {
    const trialStart = resolveTrialStart(user);
    return getTrialStatus(trialStart);
  }, [user?.firstLoginAt, user?.first_login_at]);

  // Raw subscription sources
  const [primarySub, setPrimarySub] = useState(null); // PaymentsServiceApi
  const [mirrorSub, setMirrorSub] = useState(null); // PaymentsApi mirror
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [pollTimeout, setPollTimeout] = useState(false);
  const pollingRef = useRef(null);

  // --- Normalization ---
  const normalize = useCallback((raw) => {
    if (!raw) return null;
    // Unwrap { subscription: {...} } envelope from PaymentsServiceApi.getSubscription
    const base = (raw.subscription && typeof raw.subscription === 'object'
      && !raw.status && !raw.subscriptionStatus && !raw.state) ? raw.subscription : raw;
    const statusValue = base.status || base.subscriptionStatus || base.state || 'unknown';
    const normalizedStatus = typeof statusValue === 'string' ? statusValue.toLowerCase() : statusValue;
    const subscriptionId = base.subscriptionId || base.id || base.providerSubscriptionId || base.stripeSubscriptionId || null;
    // Extract price identifier (direct field or first item price)
    const itemPrice = base.items?.[0]?.price?.id || base.items?.[0]?.price;
    const priceId = base.priceId || base.price_id || itemPrice || null;
    return {
      ...base,
      subscriptionId,
      status: normalizedStatus,
      planAlias: base.planAlias || base.plan_alias || base.priceId || base.plan || base.priceKey || base.productKey || null,
      priceId,
      tier: base.tier || base.tierFromBackend || null,  // Preserve backend's tier field (including legacy- prefix)
      tierFromBackend: base.tierFromBackend || base.tier || null,  // Explicit field for tier from backend
      cancelAtPeriodEnd: base.cancelAtPeriodEnd ?? base.cancel_at_period_end ?? base.cancel_at ?? false,
      currentPeriodEnd: base.currentPeriodEnd || base.current_period_end || base.currentPeriodEndAt || base.periodEnd || base.currentPeriodEndDate || null
    };
  }, []);

  const normalizedPrimary = useMemo(() => normalize(primarySub), [primarySub, normalize]);
  const normalizedMirror = useMemo(() => normalize(mirrorSub), [mirrorSub, normalize]);

  // Display subscription: prefer mirror when primary missing or lacks id
  // If both are null and we're initialized, return a default free-tier object
  const displaySub = useMemo(() => {
    if (normalizedMirror?.subscriptionId && !normalizedPrimary?.subscriptionId) return normalizedMirror;
    const sub = normalizedPrimary || normalizedMirror;
    
    // If no subscription found but we're initialized and payments are enabled,
    // return a default free-tier subscription object
    if (!sub && initialized && paymentsEnabled) {
      console.log('[SubscriptionContext] No subscription found, returning default free-tier object');
      return {
        subscriptionId: null,
        status: 'none',
        planAlias: 'free',
        tier: 'free',
        tierFromBackend: 'free',
        amount: 0,
        cancelAtPeriodEnd: false,
        currentPeriodEnd: null
      };
    }
    
    return sub;
  }, [normalizedPrimary, normalizedMirror, initialized, paymentsEnabled]);

  const status = displaySub?.status || (paymentsEnabled ? 'none' : 'disabled');
  const activeStatuses = ['active', 'trialing', 'none'];
  const isActive = activeStatuses.includes(status);

  // --- Fetch helpers ---
  const fetchPrimary = useCallback(async () => {
    try {
      const s = await PaymentsServiceApi.getSubscription();
      const subscriptionObj = s?.subscription || s; // unwrap if wrapped
      setPrimarySub(subscriptionObj || null);
      return subscriptionObj || null;
    } catch (e) {
      setPrimarySub(null);
      return null; // swallow; mirror may still succeed
    }
  }, []);

  const fetchMirror = useCallback(async (uid) => {
    if (!uid) return null;
    try {
      const resp = await PaymentsApi.getSubscription(uid);
      
      // 404 means user has no subscription record in the mirror - this is normal for free tier users
      if (resp.status === 404) {
        setMirrorSub(null);
        return null;
      }
      
      let json = null; try { json = await resp.json(); } catch { json = null; }
      if (!resp.ok) {
        console.log('[SubscriptionContext] Mirror fetch error:', json?.error || json?.message || `Mirror fetch failed (${resp.status})`);
        throw new Error(json?.error || json?.message || `Mirror fetch failed (${resp.status})`);
      }
      console.log('[SubscriptionContext] Mirror subscription fetched:', {
        json,
        subscriptionId: json?.subscriptionId,
        status: json?.status,
        planAlias: json?.planAlias,
        amount: json?.amount
      });
      setMirrorSub(json || null);
      return json || null;
    } catch (e) {
      // Log non-404 errors for debugging
      if (e.message && !e.message.includes('404')) {
        console.log('[SubscriptionContext] Mirror fetch error:', e.message);
      }
      setMirrorSub(null);
      return null;
    }
  }, []);

  const dualFetch = useCallback(async () => {
    if (!paymentsEnabled) return { primary: null, mirror: null };
    const uid = user?.userId || user?.id || userId;
    // Fetch primary first; only fetch mirror if primary missing or lacks subscriptionId
    const primary = await fetchPrimary();
    let mirror = null;
    if (!primary || !(primary.subscriptionId || primary.id)) {
      mirror = await fetchMirror(uid);
    }
    return { primary, mirror };
  }, [paymentsEnabled, user?.userId, user?.id, userId, fetchPrimary, fetchMirror]);

  const refresh = useCallback(async () => {
    if (!paymentsEnabled) return null;
    setLoading(true); setError(null);
    try {
      const { primary, mirror } = await dualFetch();
      return normalize(primary || mirror);
    } catch (e) {
      setError(e.message || 'Subscription refresh failed');
      return null;
    } finally {
      setLoading(false);
      if (!initialized) setInitialized(true);
    }
  }, [dualFetch, normalize, paymentsEnabled, initialized]);

  // Initial load on mount / user change
  useEffect(() => {
    if (!paymentsEnabled) {
      console.log('[SubscriptionContext] Payments disabled, marking as initialized with null subscription');
      setInitialized(true);
      return;
    }
    // Avoid firing before user known post login; userId presence acts as trigger
    const uid = user?.userId || user?.id || userId;
    if (!uid) {
      console.log('[SubscriptionContext] No user ID yet, waiting...');
      return;
    }
    console.log('[SubscriptionContext] Loading subscription for user:', uid);
    refresh();
  }, [paymentsEnabled, user?.userId, user?.id, userId, refresh]);

  // --- Polling for activation after checkout success ---
  const pollOnce = useCallback(async () => {
    const uid = user?.userId || user?.id || userId;
    const primary = await fetchPrimary();
    if (uid) await fetchMirror(uid); // keep mirror fresh too
    const normalized = normalize(primary || mirrorSub);
    const terminalStatuses = ['active','trialing','past_due','incomplete','incomplete_expired','canceled','unpaid','paused'];
    if (normalized && terminalStatuses.includes(normalized.status)) {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    }
  }, [fetchPrimary, fetchMirror, user?.userId, user?.id, userId, normalize, mirrorSub]);

  const startActivationPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollOnce, 3000);
    setPollTimeout(false);
    // Hard stop after 2 minutes
    setTimeout(() => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPollTimeout(true);
      }
    }, 120000);
  }, [pollOnce]);

  // Cleanup polling on unmount
  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  const value = {
    loading,
    initialized,
    error,
    status,
    isActive,
    subscription: displaySub,
    primarySubscription: normalizedPrimary,
    mirrorSubscription: normalizedMirror,
    pollTimeout,
    refresh, // Expose refresh for components like FirstLoginPlanPicker
    refreshSubscription: refresh, // Alias for clarity
    startActivationPolling,
    activeStatuses,
    // Free trial
    trialStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}
